import { BreadcrumbHeading, Paper, Spinner, Typography } from '@weareopenr/component-library';
import styles from './ProductPublicationStatus.module.scss';
import { useMemo } from 'react';
import { formatDate } from '../../helpers/formatDate/formatDate';
import { formatTime } from '../../helpers/formatTime/formatTime';
import { Routes } from 'src/routes';
import { useEvent } from 'src/hooks/useEvent';
import { IEvent, EVENT_TYPES, ENTITY_TYPES, NotificationStatus } from 'src/api/getEvents';
import { useExternalSystems } from 'src/hooks/useExternalSystems/useExternalSystems';
import { useParams, useLocation } from 'react-router-dom';
import { capitalise } from 'src/helpers/capitalise';
import { StatusIcon } from 'src/components/StatusIcon';

interface ExternalSystemGroup {
  name: string;
  productEvent?: IEvent;
  pricingEvent?: IEvent;
}

const formatDateTime = (date: Date) => `${formatDate(date)}, ${formatTime(date)}`;

const isExternalSystemEvent = (child: IEvent) =>
  (child.event_type === EVENT_TYPES.PUBLISH_PRICING || child.event_type === EVENT_TYPES.PUBLISH_PRODUCT) &&
  child.entity_type === ENTITY_TYPES.EXTERNAL_SYSTEM;

export const ProductPublicationStatus = () => {
  const { eventId } = useParams<{ eventId?: string }>();
  const location = useLocation();
  const { data: event, isLoading } = useEvent(eventId);
  const { data: externalSystems, isLoading: externalSystemsLoading } = useExternalSystems();

  const venuesMap = useMemo(() => {
    if (!externalSystems) return new Map<string, string>();

    const map = new Map<string, string>();
    externalSystems.forEach((system) => {
      system.venues.forEach((venue) => {
        map.set(venue.venue_id, venue.name);
      });
    });
    return map;
  }, [externalSystems]);

  const externalSystemsMap = useMemo(() => {
    if (!externalSystems) return new Map<string, string>();

    const map = new Map<string, string>();
    externalSystems.forEach((system) => {
      map.set(system.aggregatorId, system.name);
    });
    return map;
  }, [externalSystems]);
  const loading = isLoading || externalSystemsLoading;

  const lastPublishedDate = useMemo(() => {
    return event?.updated_at &&
      (event?.status === NotificationStatus.SENT || event.status === NotificationStatus.PARTIAL_SUCCESS)
      ? new Date(event.updated_at)
      : null;
  }, [event]);

  const externalSystemGroups = useMemo(() => {
    if (!event?.child_events) return new Map<string, ExternalSystemGroup>();

    const groups = new Map<string, ExternalSystemGroup>();
    const eventsBySystem = new Map<string, IEvent[]>();
    event.child_events.filter(isExternalSystemEvent).forEach((childEvent) => {
      if (!childEvent.entity_id) return;

      const existing = eventsBySystem.get(childEvent.entity_id);
      if (existing) {
        existing.push(childEvent);
      } else {
        eventsBySystem.set(childEvent.entity_id, [childEvent]);
      }
    });

    eventsBySystem.forEach((systemEvents, externalSystemId) => {
      const pricingEvent = systemEvents.find((e) => e.event_type === EVENT_TYPES.PUBLISH_PRICING);
      const productEvent = systemEvents.find((e) => e.event_type === EVENT_TYPES.PUBLISH_PRODUCT);

      groups.set(externalSystemId, {
        name: externalSystemsMap.get(externalSystemId),
        productEvent,
        pricingEvent,
      });
    });

    return groups;
  }, [event, externalSystemsMap]);

  // Determine publication type
  const getPublicationType = (group: ExternalSystemGroup) => {
    const hasProduct = !!group.productEvent;
    const hasPricing = !!group.pricingEvent;

    if (hasProduct && hasPricing) {
      return 'Product & Pricing Publish';
    } else if (hasProduct) {
      return 'Product Publish';
    } else if (hasPricing) {
      return 'Pricing Publish';
    }
    return '';
  };

  return (
    <>
      <BreadcrumbHeading
        breadcrumbs={[
          { label: 'Product Library', path: Routes.PRODUCT_LIBRARY },
          { label: 'Publication Status', path: location.pathname },
        ]}
        subtitle={lastPublishedDate && `Last published: ${formatDateTime(lastPublishedDate)}`}
      />
      {loading ? (
        <div className={styles['spinner']}>
          <Spinner size="xlarge" colour="rainbow" data-testid="spinner" />
        </div>
      ) : (
        <Paper className={styles['status-container']}>
          {Array.from(externalSystemGroups.entries()).map(([externalSystemId, group]) => {
            const overallStatus = event?.status || NotificationStatus.PENDING;

            return (
              <div key={externalSystemId} className={styles['external-system-group']}>
                <div className={styles['channel']}>
                  <StatusIcon status={overallStatus} animate={overallStatus === NotificationStatus.PENDING} />
                  <div>
                    <Typography size="small" weight="semi-bold">
                      {group.name} - {getPublicationType(group)} - {capitalise(overallStatus)}
                    </Typography>
                  </div>
                </div>

                <div className={styles['events']}>
                  {group.productEvent && (
                    <div className={styles['item']}>
                      <StatusIcon
                        status={group.productEvent.status}
                        animate={group.productEvent.status === NotificationStatus.PENDING}
                      />

                      <Typography size="small">
                        Product - {capitalise(group.productEvent.status)} -
                        {` ${group.productEvent.status === NotificationStatus.SCHEDULED ? formatDateTime(new Date(group.productEvent.message.split('Publish scheduled for ')[1])) : formatDateTime(new Date(group.productEvent.updated_at))}`}
                      </Typography>
                    </div>
                  )}

                  {group.pricingEvent && (
                    <>
                      <div className={styles['item']}>
                        <StatusIcon
                          status={group.pricingEvent.status}
                          animate={group.pricingEvent.status === NotificationStatus.PENDING}
                        />
                        <div>
                          <Typography size="small">
                            Pricing - {capitalise(group.pricingEvent.status)} -
                            {` ${group.pricingEvent.status === NotificationStatus.SCHEDULED ? formatDateTime(new Date(group.pricingEvent.message.split('Publish scheduled for ')[1])) : formatDateTime(new Date(group.pricingEvent.updated_at))}`}
                          </Typography>
                        </div>
                      </div>

                      {group.pricingEvent.child_events && group.pricingEvent.child_events.length > 0 && (
                        <div className={styles['venue-list']}>
                          {group.pricingEvent.child_events.map((venueEvent) => (
                            <div key={venueEvent.event_id} className={styles['item']}>
                              <StatusIcon
                                status={venueEvent.status}
                                animate={venueEvent.status === NotificationStatus.PENDING}
                              />
                              <div>
                                <Typography size="small" weight="semi-bold">
                                  {venueEvent.entity_id && venuesMap.get(venueEvent.entity_id)}
                                </Typography>
                                <Typography size="small">
                                  {capitalise(venueEvent.status)} - {formatDateTime(new Date(venueEvent.updated_at))}
                                </Typography>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </Paper>
      )}
    </>
  );
};
